import React, { useMemo } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { ghcolors, vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { ChatMessageButton } from './ChatMessageButton';
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { isDarkMode } from '../ThemeContext';
import { insertCode, undoInsertCode } from '../../features/chatkasten/insertcode';
import { showNewProject } from '../../core/project/projectdata';
import { getDomainCfg, getStateValue } from '../../core/state';
import { getViewData } from '../../core/views';
import { VIEW_DATA } from '../../core/constants';

interface CodeBlockProps {
  id: string;
  language: string;
  code: string;
}

interface Action {
  label: string;
  icon: IconName;
  onClick: () => void;
  clickTimeout?: number;
  highlight?: boolean;
}

const CodeBlock: React.FC<CodeBlockProps> = ({ id, language, code }) => {
  const darkMode = isDarkMode();

  const getCodeActions = useMemo((): Action[] => {
    const copyAction: Action = {
      label: 'Kopieren',
      icon: 'copy',
      onClick: () => {
        navigator.clipboard.writeText(code);
      },
      clickTimeout: 2500,
    };

    const runAction: Action = {
      label: 'Ausführen',
      icon: 'play',
      onClick: async () => {
        await showNewProject(language, code);
      },
      clickTimeout: 0,
    };

    const insertAction: Action = {
      label: 'In Editor einfügen',
      icon: 'code-compare',
      onClick: async () => {
        await insertCode(language, code, id);
      },
      highlight: false,
      clickTimeout: 0,
    };

    const undoAction: Action = {
      label: 'Rückgängig',
      icon: 'undo',
      onClick: () => {
        undoInsertCode();
      },
      clickTimeout: 0,
    };

    const actions: Action[] = [copyAction];

    const hasSupportedLang = getDomainCfg().chatSettings.supportedCodeLangs?.includes(language.toLowerCase());

    const viewState = getStateValue(VIEW_DATA);
    const hasRunAction = viewState.currentView === 'chat';
    const hasInsertAction = viewState.currentView === 'editor';
    const editorState = getViewData('editor');
    const hasExecutable = editorState.project.executable !== false;

    if (hasSupportedLang && hasRunAction && hasExecutable) {    
      actions.unshift(runAction);
    }

    if (hasSupportedLang && hasInsertAction) {    
      actions.unshift(insertAction);
      
      const hasOldEditorCode = editorState.data?.oldEditorCode;
      const codeInsertId = editorState.data?.codeInsertId;
      if (hasOldEditorCode && codeInsertId === id) {
        insertAction.highlight = true;
        actions.unshift(undoAction);
      } 
    }

    return actions;
  }, [language, code]);

  const memoizedHighlighter = useMemo(
    () => (
      <SyntaxHighlighter
        style={darkMode ? vscDarkPlus as any : ghcolors as any}
        language={language}
        className="code-block-content"
        PreTag="div"
        customStyle={{              
          margin: 0, // Entfernt die äußeren Abstände
          border: 'none', // Entfernt den Rand
          boxShadow: 'none', // Entfernt jeglichen Boxschatten
        }}
      >
        {code}
      </SyntaxHighlighter>
    ),
    [code, language, darkMode]
  );  

  const simpleCodeBlock = useMemo(
    () => (
      <pre className="code-block-content" style={{ margin: 0, border: 'none', boxShadow: 'none' }}>
        <code className={`language-${language}`}>
          {code}
        </code>
      </pre>
    ),
    [code, language]
  );

  // Comment out or remove the following lines as they are no longer needed
  // const [useSimpleCode, setUseSimpleCode] = useState(false);
  // const toggleCodeView = useCallback(() => {
  //   setUseSimpleCode(prev => !prev);
  // }, []);
  // useEffect(() => {
  //   const toggleAction: Action = {
  //     icon: useSimpleCode ? 'palette' : 'code',
  //     tooltip: useSimpleCode ? 'Switch to Syntax Highlighter' : 'Switch to Simple Code View',
  //     onClick: toggleCodeView,
  //   };
  //   getCodeActions.push(toggleAction);
  // }, [getCodeActions, useSimpleCode, toggleCodeView]);

  return (
    <div className="code-block">
      <div className="code-block-header">
        <span className="code-language">{language}</span>
        <div className="code-actions-container" style={{ marginLeft: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
          {getCodeActions.map((action, index) => (
            <ChatMessageButton
              key={`${id}-action-${index}`}
              action={action}
              className="code-action-button"                  
            />
          ))}
        </div>
      </div>
      {memoizedHighlighter}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(CodeBlock);
